import React, { useMemo, useState } from 'react';
import { effectiveRsvpStatus } from '../../utils/guestRsvpStatus';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import Icon from '../Icon';
import { Guest } from '../../types/Guests';
import { mergeGuests } from '../../services/GuestService';
import { findDuplicateGroups, previewMergedFields, type DuplicateGroup } from '../../utils/guestMerge';

export interface MergeDuplicatesModalProps {
  open: boolean;
  onClose: () => void;
  guests: Guest[];
  /** Called after a successful merge so the caller can refresh + toast. */
  onMerged: (summary: { keptId: string; mergedIds: string[] }) => void;
}

export { findDuplicateGroups };

const MergeDuplicatesModal: React.FC<MergeDuplicatesModalProps> = ({ open, onClose, guests, onMerged }) => {
  const groups = useMemo(() => findDuplicateGroups(guests), [guests]);
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [keepId, setKeepId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeGroup = groups.find((g) => g.key === activeGroupKey) ?? null;
  const keeper = activeGroup?.guests.find((g) => g.id === keepId) ?? null;
  const losers = activeGroup && keeper ? activeGroup.guests.filter((g) => g.id !== keeper.id) : [];

  const selectGroup = (group: DuplicateGroup) => {
    setActiveGroupKey(group.key);
    setKeepId(group.guests[0].id);
    setError(null);
  };

  const handleConfirmMerge = async () => {
    if (!keeper || losers.length === 0) return;
    setMerging(true);
    setError(null);
    const result = await mergeGuests(keeper.id, losers.map((l) => l.id));
    setMerging(false);
    setConfirmOpen(false);
    if (!result.success) {
      setError(result.error ?? 'Failed to merge guests.');
      return;
    }
    onMerged({ keptId: keeper.id, mergedIds: losers.map((l) => l.id) });
    setActiveGroupKey(null);
    setKeepId(null);
  };

  return (
    <Modal open={open} onClose={onClose} title="Merge duplicate guests" className="merge-duplicates-modal" size="large">
      {groups.length === 0 && !activeGroup && (
        <p className="merge-duplicates__empty">No likely-duplicate guests found (matched on name).</p>
      )}

      {!activeGroup && groups.length > 0 && (
        <div className="merge-duplicates__groups">
          <p className="merge-duplicates__hint">
            These guests share the same name. Review each group and choose which record to keep — the others will be merged into it and archived.
          </p>
          {groups.map((group) => (
            <div key={group.key} className="merge-duplicates__group-row">
              <div>
                <strong>{group.guests[0].name}</strong>
                <span className="merge-duplicates__count"> — {group.guests.length} records</span>
              </div>
              <Button size="sm" variant="outline" onClick={() => selectGroup(group)}>
                Review
              </Button>
            </div>
          ))}
        </div>
      )}

      {activeGroup && (
        <div className="merge-duplicates__review">
          <button className="merge-duplicates__back" onClick={() => { setActiveGroupKey(null); setKeepId(null); }}>
            <Icon name="chevronLeft" size={14} /> Back to duplicates
          </button>

          <p className="merge-duplicates__hint">Choose the record to keep. The others will be merged into it, then archived.</p>

          <div className="merge-duplicates__candidates">
            {activeGroup.guests.map((g) => (
              <label key={g.id} className={`merge-duplicates__candidate${keepId === g.id ? ' merge-duplicates__candidate--selected' : ''}`}>
                <input
                  type="radio"
                  name="merge-keep"
                  checked={keepId === g.id}
                  onChange={() => setKeepId(g.id)}
                />
                <div>
                  <div className="merge-duplicates__candidate-name">{g.name} <span className="merge-duplicates__id">({g.id})</span></div>
                  <div className="merge-duplicates__candidate-meta">
                    RSVP: {effectiveRsvpStatus(g)} · Email: {g.email || '—'} · Phone: {g.phone || '—'}
                  </div>
                  {g.tags.length > 0 && (
                    <div className="merge-duplicates__candidate-tags">
                      {g.tags.map((t) => <span key={t.id} className="gst-tag" style={{ /* ds-dynamic: tag colour from the project's tag catalogue */ '--gst-tag-color': t.color } as React.CSSProperties}>{t.label}</span>)}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>

          {keeper && losers.length > 0 && (
            <div className="merge-duplicates__preview">
              <h4>After merge, {keeper.name} will have:</h4>
              <table className="dt merge-duplicates__preview-table">
                <tbody>
                  {previewMergedFields(keeper, losers).map((row) => (
                    <tr key={row.field}>
                      <td>{row.field}</td>
                      <td>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="merge-duplicates__preview-note">
                The other {losers.length} record{losers.length > 1 ? 's' : ''} will be archived and no longer appear in your guest list.
              </p>
            </div>
          )}

          {error && <p className="merge-duplicates__error">{error}</p>}

          <div className="merge-duplicates__actions">
            <Button variant="ghost" size="sm" onClick={() => { setActiveGroupKey(null); setKeepId(null); }}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" disabled={!keeper || losers.length === 0} onClick={() => setConfirmOpen(true)}>
              Merge {losers.length > 0 ? `${losers.length + 1} records` : ''} into {keeper?.name ?? 'selected record'}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmMerge}
        title={`Merge ${losers.length + 1} records into ${keeper?.name ?? ''}?`}
        description="This cannot be undone. The other records will be archived and their answers per event moved onto the kept record."
        confirmLabel="Merge"
        variant="danger"
        loading={merging}
      />
    </Modal>
  );
};

export default MergeDuplicatesModal;
